import {Injectable} from "@angular/core";
import {Product} from "../../models/product";
import {ScenarioImageService} from "../scenario-image/scenario-image.service";
import {DoorService} from "../door/door.service";
import {CustomerService} from "../customer/customer.service";
import {DoorType} from "../../models/door";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PopUpComponent, PopUpResult} from "./pop-up-component";
import {ScenarioImage, ScenarioType} from "../../models/scenario-image";
import {FilterService, GenericFilter} from "../filter/filter.service";
import {skip, take, combineLatestWith, filter} from 'rxjs/operators';

/**
 * Implements the validations defined in this issue
 * https://github.com/fluegelspiel/door-vision/issues/804
 */
@Injectable({providedIn: 'root'})
export class DoorTypeValidationService {

  private DEBUG = false

  private wrongProductTypeConfirmed = false

  constructor(
    private readonly scenarioImageService: ScenarioImageService,
    private readonly doorService: DoorService,
    private filterService: FilterService<GenericFilter>,
    private readonly customerService: CustomerService,
    private modalService: NgbModal
  ) {

    this.scenarioImageService.getSelected$().subscribe(scenarioImage => {
      this.scenarioChanged(scenarioImage)
    })
  }

  private hasMultiDoorsFeatureEnabled(): boolean {
    const customerConfig = this.customerService.getCustomerConfig()
    return !!customerConfig?.allowMultipleDoors
  }

  private hasEnabledValidation(): boolean {
    /*
    const customerKey = CustomerServiceImpl.GetCustomerKey()
    const enabledCustomers = ['overheaddoor', 'wayne-dalton', 'bghome', 'bango', 'demo-interior']
    return enabledCustomers.includes(customerKey.toLowerCase()) */

    // All customers should have this feature enabled
    // https://github.com/fluegelspiel/door-vision/issues/1019
    // A separate check for the hasMultiDoorsFeatureEnabled() is done in all places where it is needed
    return true
  }

  /**
   -> Intended behaviour: When the user clicks the single/double switch nothing should happen (as it is now).
   As soon as he clicks a door that doesn't match the type of the CornerSelection we should show the popup window.
   @returns true if the product selection is valid, false if the user canceled the action or clicked "back"
   **/
  public async validateNewProductSelection(product: Product): Promise<boolean> {

    // Only enable for overheaddoor
    if (!this.hasEnabledValidation()) {
      this.log('validateNewProductSelection: not an overheaddoor customer, no action needed')
      return true
    }

    // If the user already confirmed the wrong product type we don't need to ask again
    if (this.wrongProductTypeConfirmed) {
      return true
    }

    // This check is not done when the multi door visualisation feature is turned off
    if (!this.hasMultiDoorsFeatureEnabled()) {
      this.log('validateNewProductSelection: multi door visualisation disabled, not action needed')
      return true
    }

    // Fetch the needed data
    const currentScenario = this.scenarioImageService.getSelected()
    const currentlySelectedDoorCornerIndex = this.doorService.getSelectedIndex()
    const selectedDoorCorners = currentScenario?.corners.find(c => c.index === currentlySelectedDoorCornerIndex)

    this.log('validateNewProductSelection', {
      product,
      currentScenario,
      selectedDoorCorners
    })

    const showPopUp = selectedDoorCorners && product.doorType !== selectedDoorCorners.doorType

    if (!showPopUp) {
      return true
    }

    const result = await this.showPopUp()

    // Store that the user confirmed the wrong product type
    this.wrongProductTypeConfirmed = result

    return result
  }

  public async scenarioChanged(newScenario: ScenarioImage): Promise<void> {

    if (!this.hasEnabledValidation()) {
      this.log('scenarioChanged: not an overheaddoor customer, no action needed')
      return
    }

    if (!this.hasMultiDoorsFeatureEnabled()) {
      this.log('scenarioChanged: multi door visualisation disabled, not action needed')
      return
    }

    if (newScenario.type === ScenarioType.EXAMPLE || newScenario.type === ScenarioType.USER) {

      // Check if the new scenario has enough corners
      let currentlySelectedDoorCornerIndex = this.doorService.getSelectedDoorCornersIndexValue()
      if (newScenario && newScenario.corners.length < currentlySelectedDoorCornerIndex) {
        currentlySelectedDoorCornerIndex = 1
      }

      const selectedDoorCorners = newScenario?.corners.find(c => c.index === currentlySelectedDoorCornerIndex)
      const selectedDoor = this.doorService.getSelectedDoorAtIndex(currentlySelectedDoorCornerIndex)

      this.log('scenarioChanged', {
        newScenario,
        currentlySelectedDoorCornerIndex,
        selectedDoorCorners,
        selectedDoor
      })

      // If the selected door doesn't match the door type of the new scenario we load the correct door type
      if (selectedDoor && selectedDoorCorners?.doorType && selectedDoor.doorType !== selectedDoorCorners.doorType) {

        let newExternalId = selectedDoor.externalIds[0].replace(/_d$/, '')
        if (selectedDoorCorners.doorType === DoorType.GARAGE_DOUBLE) {
          newExternalId = newExternalId + '_d'
        }

        const doorToSelect = await this.doorService.getDoorByExternalId(newExternalId)

        // Execute stuff after 200 ms
        const newProductTypeSelection = 'door';
        const referralVisualDoorExternalId = newExternalId

        if (doorToSelect) {
          this.doorService.selectProductAtCurrentIndex(doorToSelect)
        }

        this.filterService.setFilter({
          selectedDoorType: selectedDoorCorners.doorType,
          referralVisualDoorExternalId,
          selectedProductType: newProductTypeSelection
        });
      }
    }
  }

  public async validateDoorTypeFilterChange(newDoorType: DoorType): Promise<boolean> {
    if (!this.hasEnabledValidation()) {
      this.log('validateDoorTypeFilterChange: not an overheaddoor or bghome customer, no action needed')
      return true
    }

    if (this.hasMultiDoorsFeatureEnabled()) {

      // If the user already confirmed the wrong product type we don't need to ask again
      if (this.wrongProductTypeConfirmed) {
        return true
      }

      const currentScenario = this.scenarioImageService.getSelected()
      if (!currentScenario) {
        return true
      }

      const currentlySelectedDoorCornerIndex = this.doorService.getSelectedIndex()
      const selectedDoorCorners = currentScenario.corners.find(c => c.index === currentlySelectedDoorCornerIndex)

      const showPopUp = currentScenario.type === ScenarioType.USER && selectedDoorCorners && newDoorType !== selectedDoorCorners.doorType

      if (!showPopUp) {
        return true
      }

      const result = await this.showPopUp()

      this.wrongProductTypeConfirmed = result

      return result
    } else {

      const currentOrientation = await this.scenarioImageService.getCurrentOrientationValue()

      if (!currentOrientation) {
        this.log('validateDoorTypeFilterChange: no current orientation found')
        return true
      }

      // Reloading the scenarios with the correct new door type
      this.scenarioImageService.reloadScenarios(currentOrientation, newDoorType)

      // Listen for the next scenario list change and select the first one
      this.scenarioImageService.getScenariosObservable().pipe(
        skip(1),
        take(1),
        combineLatestWith(this.doorService.getProductsObservable().pipe(skip(1), filter(visualDoors => visualDoors.length > 0), take(1)))
      ).subscribe(async ([scenarios, visualDoors]) => {

        // Select the first scenario
        const newScenario = scenarios.length > 0 ? scenarios[0] : undefined
        if (newScenario) {
          this.scenarioImageService.selectScenario(newScenario)
        }

        // Select the first visual door
        const newVisualDoor = visualDoors.length > 0 ? visualDoors[0] : undefined

        // Select the first visual door normally if we don't have a scenario
        if (newVisualDoor && !newScenario) {
          this.doorService.selectProductAtCurrentIndex(newVisualDoor)
        }

        if (!newScenario || !newVisualDoor) {
          console.warn('validateDoorTypeFilterChange: no new scenario or visual door found')
          return
        }

        // Select the default products for the new door type
        // https://github.com/fluegelspiel/door-vision/issues/1019

        // Only do this if the customer has enabled products and supported product types are set
        const customerConfig = this.customerService.getCustomerConfig()
        if (!customerConfig || !customerConfig.enabledProducts || !customerConfig.supportedProductTypes || customerConfig.supportedProductTypes.length === 0) {
          // Only select the door
          this.doorService.selectProductAtCurrentIndex(newVisualDoor)
          return
        }

        const productSelections: Array<{ index: number; products: Array<Product> }> = [];

        // Go over all indexes
        for (const corners of newScenario.corners) {
          const index = corners.index;
          const productsToAdd: Array<Product> = [];

          // Go over all possible product types
          for (const productType of customerConfig.supportedProductTypes) {
            if (productType === 'door') {
              productsToAdd.push(newVisualDoor);
              continue
            }

            // If not, we need to select the first product of that type
            const defaultProduct = await this.doorService.getDefaultProductForType({
              productType,
              visualDoorId: newVisualDoor.id
            });
            if (defaultProduct) {
              productsToAdd.push(defaultProduct);
            }
          }

          // If there are new products to add for this index, collect them
          if (productsToAdd.length > 0) {
            productSelections.push({index, products: productsToAdd});
          }
        }

        // Perform selection in one go
        if (productSelections.length > 0) {
          this.doorService.selectProductsAtIndex(productSelections);
        }

      })

      return true
    }
  }

  /**
   * Shows the pop up
   * @returns true if the user clicked on continue and false if the user canceled the action or clicked "back"
   */
  private async showPopUp(): Promise<boolean> {
    try {
      const modalRef = this.modalService.open(PopUpComponent);
      const result = await (modalRef.result as Promise<PopUpResult>);

      this.log('pop up result', result)
      return result.confirmed
    } catch (e) {
      // on an error consider it as a cancel
      this.log('error during pop up', e)
      return false
    }
  }

  private log(...any: any): void {
    if (!this.DEBUG) return

    console.log(...any)
  }
}
